/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * The DNS zone of a domain, in its DNS tab: which name servers the domain uses (Tino's default
 * ones, the parking pair or outside ones), and the zone the system keeps for it, if any
 * (subsystems/dns/facade/adapters/admin-api/src/admin/dns.rs). Staff edit the customer's records
 * with a reason; each change is checked by the DNS core's rules, becomes a version and is sent to
 * the DNS server by provisioning-worker. Records another service published (e-mail, website,
 * cloud) are shown read-only.
 */

import { Download, FileDown, FileUp, KeyRound, LayoutTemplate, Pencil, Plus, RotateCcw, ShieldOff, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api, isApiError } from '../../../api';
import { RECORD_TYPES, type DnsChange, type DnsEdit, type DnsImported, type DnsLogin, type DnsOperation, type DnsRecordSet, type DnsViolation, type DnsZone, type DnsZoneVersion, type ImportMode, type RecordType } from '../../../api/areas/dns';
import type { DomainDetail } from '../../../api/types';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout, DataTable, Field, Section } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { KeyValue } from '../../../components/KeyValue';
import { Loading } from '../../../components/States';
import { useWrite, WriteError } from '../../../components/Write';
import { formatDateTime } from '../../../lib/format';
import { useApi } from '../../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, selectClass, type DialogBodyProps } from '../../operator/shared';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

/** Which name servers a domain uses. */
export type NsKind = 'TINO' | 'PARKED' | 'EXTERNAL' | 'NONE';

const host = (h: string) => h.trim().toLowerCase().replace(/\.$/, '');

/** Every host in `hosts` is one of `set` (a non-empty set). */
export function within(hosts: string[], set: string[]): boolean {
  const s = new Set(set.map(host));
  return hosts.length > 0 && s.size > 0 && hosts.every((h) => s.has(host(h)));
}

/**
 * Every host in `hosts` is one of `parking` (any version) → parked; of `tino` or of the name servers
 * of the zone the system keeps (`zone`) → Tino's; else outside.
 */
export function nsKind(hosts: string[], tino: string[], parking: string[], zone: string[] = []): NsKind {
  if (!hosts.length) return 'NONE';
  if (within(hosts, parking)) return 'PARKED';
  if (within(hosts, tino) || within(hosts, zone)) return 'TINO';
  return 'EXTERNAL';
}

const KIND_TONE: Record<NsKind, Tone> = { TINO: 'ok', PARKED: 'warn', EXTERNAL: 'neutral', NONE: 'neutral' };

/** A text value as the core reads it: quoted, in strings of at most 255 characters. */
export function txtValue(raw: string): string {
  const v = raw.trim();
  if (v.startsWith('"')) return v;
  const parts: string[] = [];
  for (let i = 0; i < v.length; i += 255) parts.push(v.slice(i, i + 255));
  return parts.map((p) => `"${p.replace(/[\\"]/g, (c) => `\\${c}`)}"`).join(' ');
}

function useSource(): (owner: string | null) => string {
  const { t } = useTranslation('dns');
  return (owner) => {
    if (!owner) return t('source.customer');
    switch (owner.split(':')[0]) {
      case 'email':
        return t('source.email');
      case 'website':
        return t('source.website');
      case 'cloud':
        return t('source.cloud');
      case 'hosting':
        return t('source.hosting');
      default:
        return t('source.other', { owner });
    }
  };
}

/** The violations of a refused change (`ZONE_RECORDS_INVALID`), each with its rule in words. */
function Violations({ error }: { error: unknown }) {
  const { t } = useTranslation('dns');
  const tr = t as unknown as Lookup;
  if (!isApiError(error) || error.code !== 'ZONE_RECORDS_INVALID') return null;
  const list = ((error.details as { violations?: DnsViolation[] } | null)?.violations ?? []).slice(0, 20);
  if (!list.length) return null;
  return (
    <Callout tone="crit" title={t('form.violations')}>
      <ul className="grid gap-1">
        {list.map((v, i) => (
          <li key={i}>
            <span className="font-mono text-[13px]">
              {v.name} {v.type ?? ''}
            </span>
            : {tr(`rule.${v.rule}`, { defaultValue: v.message })}
          </li>
        ))}
      </ul>
    </Callout>
  );
}

function Failure({ error }: { error: unknown }) {
  return (
    <>
      <Violations error={error} />
      <WriteError error={error} />
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Forms

/** A name as the core keeps it: relative to the apex, lower case (`www.example.vn.` → `www`, the apex → `@`). */
export function relName(name: string, apex: string): string {
  const n = name.trim().toLowerCase().replace(/\.$/, '');
  if (!n || n === '@' || n === apex) return '@';
  return n.endsWith(`.${apex}`) ? n.slice(0, -(apex.length + 1)) : n;
}

/**
 * Adds or edits one record set of the customer. An upsert replaces the customer's records of its
 * set, so the customer's records already at the target name and type (another set than the one
 * edited) are kept in it.
 */
function RecordForm({ close, changed, zoneId, apex, existing, set, others }: DialogBodyProps & { zoneId: string; apex: string; existing: DnsRecordSet[]; set?: DnsRecordSet; others: string[] }) {
  const { t } = useTranslation('dns');
  const [name, setName] = useState(set?.name ?? '');
  const [type, setType] = useState<RecordType>(set?.type ?? 'A');
  const [ttl, setTtl] = useState(String(set?.ttl ?? 3600));
  const [values, setValues] = useState((set?.records ?? []).filter((r) => !r.owner).map((r) => r.data).join('\n'));
  const [reason, setReason] = useState('');
  const records = values.split('\n').map((v) => v.trim()).filter(Boolean).map((v) => (type === 'TXT' ? txtValue(v) : v));
  const w = useWrite((_: null, opts) => {
    const changes: DnsChange[] = [];
    const target = relName(name, apex);
    const moved = !set || set.name !== target || set.type !== type;
    if (set && moved) changes.push({ action: 'delete', name: set.name, type: set.type });
    const kept = moved ? (existing.find((x) => x.name === target && x.type === type)?.records ?? []).filter((r) => !r.owner).map((r) => r.data) : [];
    changes.push({ action: 'upsert', name: target, type, ttl: Number(ttl), records: [...new Set([...kept, ...records])] });
    return api.editDnsRecords(zoneId, { changes, reason: reason.trim() }, opts);
  });
  const done = w.state.phase === 'done' ? w.state.result.edit : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{done.ticket ? t('form.done', { version: done.version }) : t('form.unchanged')}</Callout>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-[1fr_8rem_8rem]">
            <Field label={t('form.name')} hint={t('form.nameHint')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="@" className="font-mono" maxLength={253} />
            </Field>
            <Field label={t('form.type')}>
              <select className={selectClass} value={type} onChange={(e) => setType(e.target.value as RecordType)}>
                {RECORD_TYPES.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('form.ttl')}>
              <Input type="number" min={1} value={ttl} onChange={(e) => setTtl(e.target.value)} />
            </Field>
          </div>
          <Field label={t('form.values')} hint={type === 'TXT' ? t('form.txtHint') : t('form.valuesHint')}>
            <Textarea rows={4} value={values} onChange={(e) => setValues(e.target.value)} className="font-mono text-[13px]" required />
          </Field>
          {others.length ? <p className="text-xs text-muted-foreground">{t('form.others', { list: others.join(', ') })}</p> : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <Failure error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim() || !records.length || !Number(ttl)} submit={done ? undefined : t('form.save')} onClose={close} />
    </form>
  );
}

function RemoveForm({ close, changed, zoneId, set }: DialogBodyProps & { zoneId: string; set: DnsRecordSet }) {
  const { t } = useTranslation('dns');
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.editDnsRecords(zoneId, { changes: [{ action: 'delete', name: set.name, type: set.type }], reason: reason.trim() }, opts));
  const done = w.state.phase === 'done' ? w.state.result.edit : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{done.ticket ? t('form.done', { version: done.version }) : t('form.unchanged')}</Callout>
      ) : (
        <>
          <p className="text-sm">{t('form.removeBody', { name: set.name, type: set.type })}</p>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <Failure error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={done ? undefined : t('form.removeSubmit')} danger onClose={close} />
    </form>
  );
}

/** Goes back to a version, once staff saw the customer's records it holds. */
function RollbackForm({ close, changed, zoneId, version }: DialogBodyProps & { zoneId: string; version: number }) {
  const { t } = useTranslation('dns');
  const [reason, setReason] = useState('');
  const old = useApi((signal) => api.getDnsZoneVersion(zoneId, version, { signal }), [zoneId, version]);
  const mine = old.status === 'success' ? old.data.records.flatMap((s) => s.records.filter((r) => !r.owner).map((r) => `${s.name}\t${s.ttl}\t${s.type}\t${r.data}`)) : [];
  const w = useWrite((_: null, opts) => api.rollbackDnsZone(zoneId, { version, reason: reason.trim() }, opts));
  const done = w.state.phase === 'done' ? w.state.result.edit : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{done.ticket ? t('versions.done', { from: version, version: done.version }) : t('form.unchanged')}</Callout>
      ) : (
        <>
          <p className="text-sm">{t('versions.rollbackBody', { version })}</p>
          {old.status === 'loading' ? <Loading rows={2} /> : null}
          {old.status === 'error' ? <DataError error={old.error} onRetry={old.reload} /> : null}
          {old.status === 'success' && mine.length ? (
            <div className="grid gap-1">
              <p className="text-sm font-medium">{t('versions.rollbackRecords', { version })}</p>
              <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">{mine.join('\n')}</pre>
            </div>
          ) : null}
          {old.status === 'success' && !mine.length ? <Callout tone="warn">{t('versions.rollbackEmpty', { version })}</Callout> : null}
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <Failure error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim() || old.status !== 'success'} submit={done ? undefined : t('action.rollback')} onClose={close} />
    </form>
  );
}

function ExportView({ close, zoneId, apex }: DialogBodyProps & { zoneId: string; apex: string }) {
  const { t } = useTranslation('dns');
  const { t: to } = useTranslation('operator');
  const text = useApi((signal) => api.exportDnsZone(zoneId, { signal }), [zoneId]);
  const download = (body: string) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([body], { type: 'text/dns' }));
    a.download = `${apex}.zone`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="grid gap-4">
      {text.status === 'loading' ? <Loading rows={3} /> : null}
      {text.status === 'error' ? <DataError error={text.error} onRetry={text.reload} /> : null}
      {text.status === 'success' ? <Textarea readOnly rows={14} value={text.data.text} className="font-mono text-xs" /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={close}>
          {to('common.close')}
        </Button>
        <Button type="button" disabled={text.status !== 'success'} onClick={() => text.status === 'success' && download(text.data.text)}>
          <Download /> {t('exportForm.download')}
        </Button>
      </div>
    </div>
  );
}

function ModeSelect({ value, onChange, first }: { value: ImportMode; onChange: (m: ImportMode) => void; first: { merge: string; replace: string } }) {
  return (
    <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value as ImportMode)}>
      <option value="merge">{first.merge}</option>
      <option value="replace">{first.replace}</option>
    </select>
  );
}

function ImportForm({ close, changed, zoneId }: DialogBodyProps & { zoneId: string }) {
  const { t } = useTranslation('dns');
  const tr = t as unknown as Lookup;
  const [text, setText] = useState('');
  const [mode, setMode] = useState<ImportMode>('merge');
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.importDnsZone(zoneId, { text, mode, reason: reason.trim() }, opts));
  const done: DnsImported | null = w.state.phase === 'done' ? w.state.result : null;
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <>
          <Callout tone="ok">{done.edit.ticket ? t('importForm.done', { version: done.edit.version }) : t('form.unchanged')}</Callout>
          {done.skipped.length ? (
            <Callout tone="warn" title={t('importForm.skipped', { count: done.skipped.length })}>
              <ul className="grid gap-1">
                {done.skipped.slice(0, 50).map((s) => (
                  <li key={`${s.line}-${s.owner}-${s.rtype}`}>
                    <span className="font-mono text-[13px]">
                      #{s.line} {s.owner} {s.rtype}
                    </span>
                    : {tr(`importForm.skippedReason.${s.reason}`, { defaultValue: s.reason })}
                  </li>
                ))}
              </ul>
            </Callout>
          ) : null}
          {done.warnings.length ? (
            <Callout tone="info" title={t('importForm.warnings')}>
              <ul className="grid gap-1 font-mono text-xs">
                {done.warnings.slice(0, 50).map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </Callout>
          ) : null}
        </>
      ) : (
        <>
          <Field label={t('importForm.text')}>
            <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} className="font-mono text-xs" required />
          </Field>
          <Field label={t('importForm.mode')}>
            <ModeSelect value={mode} onChange={setMode} first={{ merge: t('importForm.merge'), replace: t('importForm.replace') }} />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <Failure error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim() || !text.trim()} submit={done ? undefined : t('action.import')} onClose={close} />
    </form>
  );
}

function TemplateForm({ close, changed, zoneId }: DialogBodyProps & { zoneId: string }) {
  const { t } = useTranslation('dns');
  const tr = t as unknown as Lookup;
  const list = useApi((signal) => api.listDnsTemplates({ signal }), []);
  const [key, setKey] = useState('');
  const [vars, setVars] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<ImportMode>('merge');
  const [reason, setReason] = useState('');
  const templates = list.status === 'success' ? list.data.templates : [];
  const chosen = templates.find((x) => x.key === key);
  const w = useWrite((_: null, opts) =>
    api.applyDnsTemplate(zoneId, { key, vars: Object.fromEntries((chosen?.variables ?? []).map((v) => [v, (vars[v] ?? '').trim()])), mode, reason: reason.trim() }, opts),
  );
  const done: DnsEdit | null = w.state.phase === 'done' ? w.state.result.edit : null;
  const nameOf = (k: string, fallback: string) => tr(`templates.${k}.name`, { defaultValue: fallback });
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{done.ticket ? t('templateForm.done', { version: done.version }) : t('form.unchanged')}</Callout>
      ) : list.status === 'error' ? (
        <DataError error={list.error} onRetry={list.reload} />
      ) : list.status === 'loading' ? (
        <Loading rows={2} />
      ) : (
        <>
          <Field label={t('templateForm.template')} hint={chosen ? tr(`templates.${chosen.key}.description`, { defaultValue: chosen.description ?? '' }) : undefined}>
            <select className={selectClass} value={key} onChange={(e) => setKey(e.target.value)} required>
              <option value="" disabled>
                —
              </option>
              {templates.map((x) => (
                <option key={x.key} value={x.key}>
                  {nameOf(x.key, x.name)}
                </option>
              ))}
            </select>
          </Field>
          {chosen?.records.length ? (
            <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">{chosen.records.map((r) => `${r.name}\t${r.type}\t${r.data}`).join('\n')}</pre>
          ) : null}
          {(chosen?.variables ?? []).map((v) => (
            <Field key={v} label={tr(`vars.${v}`, { defaultValue: v })}>
              <Input value={vars[v] ?? ''} onChange={(e) => setVars({ ...vars, [v]: e.target.value })} className="font-mono" required />
            </Field>
          ))}
          <Field label={t('templateForm.mode')}>
            <ModeSelect value={mode} onChange={setMode} first={{ merge: t('templateForm.merge'), replace: t('templateForm.replace') }} />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <Failure error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim() || !chosen} submit={done ? undefined : t('action.template')} onClose={close} />
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
// The zone

/** Where the zone's changes are: on the DNS server, waiting, or failed (the latest record operation). */
// ---------------------------------------------------------------------------------------------
// The domain's DNS login (owner 2026-10-07): a partner's customer manages the zone with the
// domain name and this password, without an account (subsystems/dns/modules/engine/src/engine/login.rs).

/** 16 characters without look-alikes (0/O, 1/l/I), from the browser's CSPRNG. */
export function newDnsPassword(): string {
  const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return [...bytes].map((b) => abc[b % abc.length]).join('');
}

function SetLoginForm({ close, changed, zoneId, replacing }: DialogBodyProps & { zoneId: string; replacing: boolean }) {
  const { t } = useTranslation('dns');
  const [password, setPassword] = useState(newDnsPassword);
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.setDnsLogin(zoneId, { password, reason: reason.trim() }, opts));
  const size = [...password].length;
  const done = w.state.phase === 'done';
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok" title={t('login.done')}>
          <span className="grid gap-2">
            <span className="break-all font-mono text-[13px]">{password}</span>
            <span>{t('login.doneHint')}</span>
          </span>
        </Callout>
      ) : (
        <>
          <p className="text-sm">{t(replacing ? 'login.replaceBody' : 'login.setBody')}</p>
          <Field label={t('login.password')} hint={t('login.passwordHint')}>
            <span className="flex gap-2">
              <Input value={password} onChange={(e) => setPassword(e.target.value)} className="font-mono" maxLength={128} autoComplete="off" spellCheck={false} />
              <Button type="button" variant="outline" onClick={() => setPassword(newDnsPassword())}>
                {t('login.generate')}
              </Button>
            </span>
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim() || size < 12 || size > 128} submit={done ? undefined : t('login.save')} onClose={close} />
    </form>
  );
}

function RevokeLoginForm({ close, changed, zoneId }: DialogBodyProps & { zoneId: string }) {
  const { t } = useTranslation('dns');
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.revokeDnsLogin(zoneId, { reason: reason.trim() }, opts));
  const done = w.state.phase === 'done';
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('login.revoked')}</Callout>
      ) : (
        <>
          <p className="text-sm">{t('login.revokeBody')}</p>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={done ? undefined : t('login.revoke')} danger onClose={close} />
    </form>
  );
}

/** Whether the zone has a DNS login, who set it, and staff's set / replace / revoke. */
function LoginSection({ zoneId, login, editable, reload }: { zoneId: string; login: DnsLogin | null; editable: boolean; reload: () => void }) {
  const { t } = useTranslation('dns');
  const by = login ? (login.set_by_type === 'OPERATOR' ? (login.set_by_name ?? t('login.byStaff')) : t('login.byCustomer')) : null;
  return (
    <Section
      title={t('login.title')}
      description={t('login.hint')}
      actions={
        editable ? (
          <>
            <ActionDialog label={t(login ? 'login.replace' : 'login.set')} icon={KeyRound} title={t(login ? 'login.replace' : 'login.set')} onChanged={reload}>
              {(p) => <SetLoginForm {...p} zoneId={zoneId} replacing={login !== null} />}
            </ActionDialog>
            {login ? (
              <ActionDialog label={t('login.revoke')} icon={ShieldOff} variant="ghost" title={t('login.revoke')} onChanged={reload}>
                {(p) => <RevokeLoginForm {...p} zoneId={zoneId} />}
              </ActionDialog>
            ) : null}
          </>
        ) : null
      }
    >
      {login ? (
        <KeyValue
          columns={2}
          rows={[
            [t('login.state'), <Badge key="s" tone="ok">{t('login.on')}</Badge>],
            [t('login.setBy'), by],
            [t('login.setAt'), formatDateTime(login.set_at)],
            [t('login.sessions'), login.open_sessions],
          ]}
        />
      ) : (
        <p className="text-sm text-muted-foreground">{t('login.none')}</p>
      )}
    </Section>
  );
}

function sendStatus(zone: DnsZone, ops: DnsOperation[], t: Lookup): { tone: Tone; text: string } {
  if (zone.applied_version >= zone.desired_version) return { tone: 'ok', text: t('status.applied', { version: zone.applied_version }) };
  const last = ops.find((o) => o.op === 'apply' || o.op === 'repair' || o.op === 'create');
  if (last?.status === 'REFUSED') return { tone: 'crit', text: t('status.refused', { detail: last.refusal?.message ?? last.refusal?.code ?? '' }) };
  if (last?.status === 'FAILED' || (last?.status === 'QUEUED' && last.failure)) return { tone: 'crit', text: t('status.failed', { detail: last.failure?.detail ?? last.failure?.class ?? last.attention ?? '' }) };
  if (last?.status === 'UNKNOWN') return { tone: 'warn', text: t('status.unknown') };
  return {
    tone: 'pending',
    text: zone.applied_version === 0 ? t('status.waitingFirst', { desired: zone.desired_version }) : t('status.waiting', { desired: zone.desired_version, applied: zone.applied_version }),
  };
}

const OP_TONE: Record<DnsOperation['status'], Tone> = { QUEUED: 'pending', SENDING: 'pending', RUNNING: 'pending', SUCCEEDED: 'ok', REFUSED: 'crit', FAILED: 'crit', UNKNOWN: 'warn', CANCELLED: 'neutral' };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The records of a zone the system keeps (its id is its service's: a domain's DNS tab, or a DNS service). */
export function DnsZoneEditor({ zoneId }: { zoneId: string }) {
  const { t } = useTranslation('dns');
  const { t: ts } = useTranslation('subscriptions');
  const tr = t as unknown as Lookup;
  const trs = ts as unknown as Lookup;
  const source = useSource();
  const detail = useApi((signal) => api.getDnsZone(zoneId, { signal }), [zoneId]);
  if (detail.status === 'loading') return <Loading rows={4} />;
  if (detail.status === 'error') return <DataError error={detail.error} onRetry={detail.reload} title={t('loadFailed')} />;
  const { zone, login, records, versions, operations } = detail.data;
  const apex = zone.apex;
  const editable = zone.kind === 'PRIMARY' && zone.state !== 'TERMINATED';
  const status = sendStatus(zone, operations, tr);
  const note = (v: DnsZoneVersion) => {
    if (!v.note) return null;
    const back = /^to version (\d+)$/.exec(v.note);
    if (back) return t('versions.toVersion', { version: Number(back[1]) });
    if (v.reason === 'PUBLISH' || v.reason === 'WITHDRAW') return source(v.note);
    if (v.reason === 'TEMPLATE') return tr(`templates.${v.note}.name`, { defaultValue: v.note });
    return v.note;
  };
  return (
    <>
      <Section
        title={t('zone.title')}
        description={t('zone.hint')}
        flush
        actions={
          editable ? (
            <>
              <ActionDialog label={t('action.add')} icon={Plus} title={t('form.addTitle')} wide onChanged={detail.reload}>
                {(p) => <RecordForm {...p} zoneId={zoneId} apex={apex} existing={records} others={[]} />}
              </ActionDialog>
              <ActionDialog label={t('action.template')} icon={LayoutTemplate} title={t('templateForm.title')} wide onChanged={detail.reload}>
                {(p) => <TemplateForm {...p} zoneId={zoneId} />}
              </ActionDialog>
              <ActionDialog label={t('action.import')} icon={FileUp} title={t('importForm.title')} wide onChanged={detail.reload}>
                {(p) => <ImportForm {...p} zoneId={zoneId} />}
              </ActionDialog>
              <ActionDialog label={t('action.export')} icon={FileDown} title={t('exportForm.title')} description={t('exportForm.hint')} wide>
                {(p) => <ExportView {...p} zoneId={zoneId} apex={apex} />}
              </ActionDialog>
            </>
          ) : (
            <ActionDialog label={t('action.export')} icon={FileDown} title={t('exportForm.title')} description={t('exportForm.hint')} wide>
              {(p) => <ExportView {...p} zoneId={zoneId} apex={apex} />}
            </ActionDialog>
          )
        }
      >
        <div className="grid gap-4 px-5 pb-4">
          <Callout tone={status.tone}>{status.text}</Callout>
          {zone.kind === 'SECONDARY' ? <Callout tone="info">{t('zone.secondary')}</Callout> : null}
          <KeyValue
            columns={2}
            rows={[
              [t('zone.state'), trs(`service.${zone.state}`, { defaultValue: zone.state })],
              [t('zone.module'), <span key="m" className="font-mono text-[13px]">{`${zone.module} / ${zone.instance_key}`}</span>],
              [t('zone.nameservers'), zone.nameservers.length ? <span key="ns" className="font-mono text-[13px]">{zone.nameservers.join(', ')}</span> : t('zone.notYet')],
              [t('zone.dnssec'), t(`dnssecState.${zone.dnssec}`)],
            ]}
          />
        </div>
        <DataTable<DnsRecordSet>
          columns={[
            { key: 'name', header: t('col.name'), cell: (s) => <span className="font-mono text-[13px]">{s.name}</span> },
            { key: 'type', header: t('col.type'), cell: (s) => <Badge tone="neutral" icon={false}>{s.type}</Badge> },
            { key: 'ttl', header: t('col.ttl'), numeric: true, cell: (s) => s.ttl },
            {
              key: 'value',
              header: t('col.value'),
              className: 'min-w-64 whitespace-normal',
              cell: (s) => (
                <ul className="grid gap-1">
                  {s.records.map((r) => (
                    <li key={`${r.owner ?? ''}-${r.data}`} className="flex flex-wrap items-center gap-2">
                      <span className="break-all font-mono text-[13px]">{r.data}</span>
                      {r.owner ? (
                        <Badge tone="info" icon={false} title={t('readOnly', { source: source(r.owner) })}>
                          {source(r.owner)}
                        </Badge>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ),
            },
            {
              key: 'actions',
              header: '',
              cell: (s) =>
                editable && s.records.some((r) => !r.owner) ? (
                  <span className="flex gap-1">
                    <ActionDialog label={t('action.edit')} icon={Pencil} variant="ghost" title={t('form.editTitle', { name: s.name, type: s.type })} wide onChanged={detail.reload}>
                      {(p) => <RecordForm {...p} zoneId={zoneId} apex={apex} existing={records} set={s} others={s.records.filter((r) => r.owner).map((r) => `${r.data} (${source(r.owner)})`)} />}
                    </ActionDialog>
                    <ActionDialog label={t('action.remove')} icon={Trash2} variant="ghost" title={t('form.removeTitle', { name: s.name, type: s.type })} onChanged={detail.reload}>
                      {(p) => <RemoveForm {...p} zoneId={zoneId} set={s} />}
                    </ActionDialog>
                  </span>
                ) : null,
            },
          ]}
          rows={records}
          rowKey={(s) => `${s.name}/${s.type}`}
          empty={<p className="text-sm text-muted-foreground">{t('empty')}</p>}
        />
      </Section>
      {zone.platform ? null : <LoginSection zoneId={zoneId} login={login} editable={zone.state !== 'TERMINATED'} reload={detail.reload} />}
      <Section title={t('versions.title')} description={t('versions.hint')} flush>
        <DataTable<DnsZoneVersion>
          columns={[
            {
              key: 'version',
              header: t('versions.col.version'),
              cell: (v) => (
                <span className="inline-flex items-center gap-2 tabular">
                  {v.version}
                  {v.version === zone.desired_version ? (
                    <Badge tone="ok" icon={false}>
                      {t('versions.current')}
                    </Badge>
                  ) : null}
                </span>
              ),
            },
            { key: 'reason', header: t('versions.col.reason'), cell: (v) => t(`reason.${v.reason}`) },
            { key: 'staffReason', header: t('versions.col.staffReason'), className: 'whitespace-normal', cell: (v) => v.staff_reason ?? <span className="text-muted-foreground">—</span> },
            { key: 'note', header: t('versions.col.note'), className: 'whitespace-normal', cell: (v) => note(v) ?? <span className="text-muted-foreground">—</span> },
            { key: 'by', header: t('versions.col.by'), cell: (v) => v.actor_name ?? (UUID.test(v.actor_id) ? <span className="font-mono text-xs">{v.actor_id}</span> : t('versions.system')) },
            { key: 'at', header: t('versions.col.at'), className: 'whitespace-nowrap', cell: (v) => formatDateTime(v.created_at) },
            {
              key: 'rollback',
              header: '',
              cell: (v) =>
                editable && v.version !== zone.desired_version ? (
                  <ActionDialog label={t('action.rollback')} icon={RotateCcw} variant="ghost" title={t('versions.rollbackTitle', { version: v.version })} onChanged={detail.reload}>
                    {(p) => <RollbackForm {...p} zoneId={zoneId} version={v.version} />}
                  </ActionDialog>
                ) : null,
            },
          ]}
          rows={versions}
          rowKey={(v) => String(v.version)}
        />
      </Section>
      {operations.length ? (
        <Section title={t('ops.title')} flush>
          <DataTable<DnsOperation>
            columns={[
              { key: 'op', header: t('ops.col.op'), cell: (o) => tr(`opName.${o.op}`, { defaultValue: o.op }) },
              { key: 'status', header: t('ops.col.status'), cell: (o) => <Badge tone={OP_TONE[o.status]}>{t(`op.${o.status}`)}</Badge> },
              { key: 'attempts', header: t('ops.col.attempts'), numeric: true, cell: (o) => `${o.attempts}/${o.max_attempts}` },
              { key: 'at', header: t('ops.col.at'), className: 'whitespace-nowrap', cell: (o) => formatDateTime(o.created_at) },
              {
                key: 'detail',
                header: t('ops.col.detail'),
                className: 'whitespace-normal text-xs',
                cell: (o) => o.refusal?.message ?? o.refusal?.code ?? o.failure?.detail ?? o.attention ?? <span className="text-muted-foreground">—</span>,
              },
            ]}
            rows={operations}
            rowKey={(o) => o.id}
          />
        </Section>
      ) : null}
    </>
  );
}

/**
 * The DNS zone part of the DNS tab: the kind of name servers the domain uses and, when the system
 * keeps the domain's zone, its records.
 */
export function DnsZonePart({ detail, hosts }: { detail: DomainDetail; hosts: string[] }) {
  const { t } = useTranslation('dns');
  const name = detail.domain.ascii_name;
  const customer = detail.domain.customer_id;
  const known = useApi((signal) => Promise.all([api.getDefaultNameservers({ signal }), api.getParkingNameservers({ signal })]), []);
  const found = useApi((signal) => api.findDnsZone(name, customer, { signal }), [name, customer]);
  const union = (v: { versions: { nameservers: string[] }[] }) => v.versions.flatMap((x) => x.nameservers);
  // The name servers the DNS server gave the zone, once it did: the domain must use them.
  const zoneNs = found.status === 'success' ? (found.data.zone?.nameservers ?? []) : [];
  const kind = known.status === 'success' ? nsKind(hosts, union(known.data[0]), union(known.data[1]), zoneNs) : null;
  const list = hosts.join(', ');
  // Staff of one customer may not read Tino's name server sets (global settings): the kind stays unknown.
  const hint = kind ? t(`ns.kindHint.${kind}`, { list }) : known.status === 'error' ? t('ns.kindUnknown', { list: list || '—' }) : undefined;
  return (
    <>
      <Section
        title={t('ns.title')}
        description={hint}
        actions={kind ? <Badge tone={KIND_TONE[kind]}>{t(`ns.kind.${kind}`)}</Badge> : null}
      >
        {found.status === 'loading' ? <Loading rows={2} /> : null}
        {found.status === 'error' ? <DataError error={found.error} onRetry={found.reload} title={t('loadFailed')} /> : null}
        {found.status === 'success' && !found.data.zone && known.status !== 'loading' ? <Callout>{t(`noZone.${kind ?? 'NONE'}`)}</Callout> : null}
        {found.status === 'success' && found.data.zone ? (
          <KeyValue rows={[[t('zone.service'), <span key="z" className="font-mono text-[13px]">{found.data.zone.apex}</span>]]} />
        ) : null}
        {zoneNs.length > 0 && !within(hosts, zoneNs) ? (
          <Callout tone="warn" className="mt-4">
            {t('zone.notPointed', { list: zoneNs.join(', ') })}
          </Callout>
        ) : null}
      </Section>
      {found.status === 'success' && found.data.zone ? <DnsZoneEditor zoneId={found.data.zone.service_id} /> : null}
    </>
  );
}
